import axios from "axios";
// Types only — erased at compile time, so importing them costs nothing.
import type { Cell, Column } from "write-excel-file/browser";

import { Transaction, TransactionFilters } from "../../features/auth/store/types/transaction-types";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Rows pulled per request. The API caps nothing, but asking for everything in
 * one call risks a gateway timeout on a large date range — and gives the user
 * no feedback while they wait. Paging in blocks keeps each request quick and
 * lets us report progress.
 */
const CHUNK_SIZE = 500;

/**
 * Hard ceiling on pages walked — a guard against a malformed `last_page`
 * spinning the browser forever. At CHUNK_SIZE this allows 200,000 rows; if an
 * export ever reaches it the result is reported as truncated rather than
 * silently short.
 */
const MAX_PAGES = 400;

export type ExportProgress = {
  fetched: number;
  total: number;
};

export type ExportResult = {
  rows: Transaction[];
  /** True when MAX_PAGES cut the walk short and rows are missing. */
  truncated: boolean;
  /** Row count the API reported as matching the filters. */
  reportedTotal: number;
};

type FetchAllArgs = {
  filters: TransactionFilters;
  token: string;
  onProgress?: (progress: ExportProgress) => void;
  signal?: AbortSignal;
};

/**
 * Pulls every transaction matching the filters currently applied in the UI.
 *
 * Deliberately ignores `filters.page` and `filters.per_page`: the export is
 * meant to cover the whole filtered set, not the fifteen rows that happen to
 * be on screen.
 */
export async function fetchAllTransactions({
  filters,
  token,
  onProgress,
  signal,
}: FetchAllArgs): Promise<ExportResult> {
  const endpoint =
    filters.source === "archived" ? "/transactions/archived" : "/transactions";

  const rows: Transaction[] = [];
  let page = 1;
  let lastPage = 1;
  let reportedTotal = 0;

  do {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      // `page`/`per_page` are set below; `source` picks the endpoint, not a filter.
      if (key === "page" || key === "per_page" || key === "source") return;
      if (value === undefined || value === null || value === "" || value === "all") return;
      params.append(key, String(value));
    });

    params.append("page", String(page));
    params.append("per_page", String(CHUNK_SIZE));

    const res = await axios.get(`${API_URL}${endpoint}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });

    const payload = res.data?.data;
    const batch: Transaction[] = payload?.data ?? [];
    rows.push(...batch);

    lastPage = payload?.last_page ?? 1;
    reportedTotal = payload?.total ?? rows.length;
    onProgress?.({ fetched: rows.length, total: reportedTotal });

    // An empty page means there is nothing further to walk, whatever last_page claims.
    if (batch.length === 0) break;

    page += 1;
  } while (page <= lastPage && page <= MAX_PAGES);

  return {
    rows,
    truncated: page > MAX_PAGES && page <= lastPage,
    reportedTotal,
  };
}

/** One styled header cell; every column uses the same treatment. */
const HEADER = (title: string): Cell => ({
  value: title,
  type: String,
  fontWeight: "bold",
  backgroundColor: "#F4F4F5",
  align: "left",
});

/** `2026-08-26T14:03:11.000000Z` → a real Date, or undefined if unparseable. */
function toDate(value?: string): Date | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Prices arrive as strings; Excel needs a number to sum or filter on them. */
function toNumber(value?: string): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Column definitions.
 *
 * `type` is what makes Excel treat the amount column as a number you can SUM
 * and the timestamps as real dates you can sort chronologically, rather than
 * as text that merely looks like them.
 */
const columns: Column<Transaction>[] = [
  {
    header: HEADER("Transaction ID"),
    width: 14,
    cell: (t) => ({ type: Number, value: t.id }),
  },
  {
    header: HEADER("Reference"),
    width: 26,
    cell: (t) => ({ type: String, value: t.reference || "" }),
  },
  {
    header: HEADER("User ID"),
    width: 14,
    cell: (t) => ({ type: String, value: String(t.user_id ?? "") }),
  },
  {
    header: HEADER("Type"),
    width: 18,
    cell: (t) => ({ type: String, value: t.type_text || "" }),
  },
  {
    header: HEADER("Status"),
    width: 14,
    cell: (t) => ({ type: String, value: t.status_text || "" }),
  },
  {
    header: HEADER("Amount"),
    width: 14,
    cell: (t) => ({ type: Number, value: toNumber(t.price), format: "#,##0.00" }),
  },
  {
    header: HEADER("Payment Method"),
    width: 16,
    cell: (t) => ({ type: String, value: t.payment_method || "" }),
  },
  {
    header: HEADER("Provider"),
    width: 18,
    cell: (t) => ({ type: String, value: t.provider || "" }),
  },
  {
    header: HEADER("Category"),
    width: 18,
    cell: (t) => ({ type: String, value: t.category || "" }),
  },
  {
    header: HEADER("Description"),
    width: 40,
    cell: (t) => ({ type: String, value: t.description || "" }),
  },
  {
    header: HEADER("Recipient / Number"),
    width: 20,
    cell: (t) => ({ type: String, value: t.unique_number || "" }),
  },
  {
    header: HEADER("Created At"),
    width: 20,
    cell: (t) => ({
      type: Date,
      value: toDate(t.created_at),
      format: "yyyy-mm-dd hh:mm:ss",
    }),
  },
  {
    header: HEADER("Updated At"),
    width: 20,
    cell: (t) => ({
      type: Date,
      value: toDate(t.updated_at),
      format: "yyyy-mm-dd hh:mm:ss",
    }),
  },
];

/** `transactions-active-2026-08-26.xlsx` */
export function buildFileName(source: TransactionFilters["source"]): string {
  const today = new Date().toISOString().slice(0, 10);
  return `transactions-${source === "archived" ? "archived" : "active"}-${today}.xlsx`;
}

/**
 * Writes the workbook and hands it to the browser as a download.
 *
 * The xlsx writer is pulled in dynamically so it lands in its own chunk rather
 * than the main bundle — exporting is an occasional action, and every admin
 * page load would otherwise pay for it.
 */
export async function writeTransactionsWorkbook(
  rows: Transaction[],
  source: TransactionFilters["source"],
): Promise<void> {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");

  await writeXlsxFile(rows, {
    columns,
    sheet: source === "archived" ? "Archived Transactions" : "Transactions",
    // Keeps the header visible while scrolling a long export.
    stickyRowsCount: 1,
  }).toFile(buildFileName(source));
}
